/* ---------------------------------------------------------------------------
   Bridge Rating's state, and the actions that move it.

   A SEPARATE STORE FROM BRIDGE WATCH'S, on purpose. The two applications share
   a deployment, a session, a users table and a register — which is the whole
   point of building the second one here — but they do not share a screen, a
   route or a selection. Bridge Watch's store carries a map view, three 3D
   scenes, an inspection draft, a defect draft and a modeller viewport, and none
   of that means anything to a rating. Importing it would make every field of a
   rating form a reason to re-render a WebGL scene.

   What IS shared is everything below the state: core/dom.ts, components/
   widgets.ts, the design tokens, and the tenant profile. Those are store-free
   and this application uses them unchanged.

   ONE RENDER PASS, RECONCILED. Same arrangement as Bridge Watch — the whole
   the shell is rebuilt on every change and `render` diffs it against what is on
   screen, so the caret stays in the field being typed into. See core/dom.ts.
   --------------------------------------------------------------------------- */

import type { Bridge, RegisterDefect } from '../data/types.ts';
import type { Capability } from '../data/roles.ts';
import { DPWH_CBPLR, type StandardsProfile, type VehicleId } from '../cbplr/index.ts';
import type { ModelFillReport, RatingRecord } from './api.ts';

export type Screen = 'structures' | 'rate' | 'ratings' | 'method';

export const SCREENS: readonly Screen[] = ['structures', 'rate', 'ratings', 'method'];

/**
 * Where a value in the rating form came from.
 *
 * The platform rule, and the reason it is a per-field record rather than one
 * flag on the rating: a section transcribed from a drawing whose reinforcement
 * was assumed by era is exactly the ordinary case, and a rating that reported
 * itself as "from drawing" would be overstating half of itself.
 *
 * The five the architecture names, plus `standard`: a value this structure's
 * documents do not state and a published rating standard supplies — an
 * allowable stress from the material grade, an undamaged section before the
 * inspection says otherwise. Kept apart from `model` so a report never passes a
 * code default off as something the design said. The API accepts no others.
 */
export type SourceTier = 'model' | 'drawing' | 'measured' | 'era' | 'standard' | 'user' | 'inspection';

/* `inspection` (LC-20): a damage input the register's open defects gave, or an
   engineer's *inspected, no loss found* — each with its basis on the draft. */
export const SOURCE_TIERS: readonly SourceTier[] = ['model', 'drawing', 'measured', 'era', 'standard', 'user', 'inspection'];

export const SOURCE_LABEL: Readonly<Record<SourceTier, string>> = {
  model: 'From the cloud model',
  drawing: 'From the as-built drawing',
  measured: 'Measured on site',
  era: 'Assumed from the year built',
  standard: 'Standard default — not stated for this structure',
  user: 'Entered by the user',
  inspection: 'From the inspection — an issue on this member, or none found',
};

/** The short mark drawn beside a value, matching the register's own convention. */
export const SOURCE_MARK: Readonly<Record<SourceTier, string>> = {
  model: 'M',
  drawing: '',
  measured: 'S',
  era: 'E',
  standard: 'C',
  user: 'U',
  inspection: 'I',
};

/**
 * The rating form, as text.
 *
 * EVERY FIELD IS A STRING, and that is not laziness. An empty field must stay
 * empty — the engine's rule, and the platform's — and a numeric field typed as
 * `number | null` collapses "" and 0 in every code path that touches it. Held as
 * typed, converted once, at the moment the form is complete enough to compute.
 * A half-typed "0." is a legitimate intermediate state and a number cannot hold
 * it.
 */
/** The families this build can rate. Kept in step with `IMPLEMENTED`. */
export type RatingFamily = 'RCFS' | 'RCDG' | 'PCDG';

/** The families this build offers, in the order the picker draws them. */
export const FAMILIES: readonly RatingFamily[] = ['RCFS', 'RCDG', 'PCDG'];

export const FAMILY_LABEL: Readonly<Record<RatingFamily, string>> = {
  RCFS: 'RCFS — RC flat slab',
  RCDG: 'RCDG — RC deck girder (T-beam)',
  PCDG: 'PCDG — prestressed concrete I-girder',
};

/** Which girder is being rated. Null for the families that have none. */
export type GirderPosition = 'interior' | 'exterior';

export interface RatingDraft {
  bridgeId: string | null;
  family: RatingFamily;
  continuity: 'simple';
  /**
   * Interior or exterior, for the girder families.
   *
   * Null on RCFS and refused by the API there — a flat slab has no girders, and
   * a position on one describes a different bridge from the one being rated.
   */
  position: GirderPosition | null;
  spanIndex: string;
  fields: Record<string, string>;
  sources: Record<string, SourceTier>;
  note: string;
  /** The accepted inspection the damage inputs are filled against (LC-20), or null. */
  inspectionId: string | null;
  /** Per damage field marked `inspection`: the defect id it came from, or `no-loss`. */
  damageBasis: Record<string, string>;
}

/** The fields the RCFS form collects, in the order the workbook asks for them. */
export interface FieldSpec {
  /**
   * Where the value goes in the input, as a dotted path.
   *
   * A path rather than a key because the material values are nested two deep —
   * `concrete.allowable.inventory` — and flattening them into the form's own bag
   * is what lets one control render every field and one function assemble the
   * input. `setPath`/`readPath` in ./input.ts are the only things that interpret
   * it.
   */
  key: string;
  label: string;
  unit: string;
  /** The workbook cell this field is, for the reader checking against it. */
  cell: string;
  /** One sentence, where the field is not self-explanatory. */
  hint?: string;
}

export const RCFS_FIELDS: readonly FieldSpec[] = [
  { key: 'span', label: 'Span length L', unit: 'm', cell: 'LAYOUT!F33' },
  { key: 'slabThickness', label: 'Slab thickness h', unit: 'm', cell: 'LAYOUT!F32' },
  {
    key: 'slabWidth',
    label: 'Slab width bw',
    unit: 'm',
    cell: 'LAYOUT!F31',
    hint: 'The width the dead load is spread over — not the carriageway.',
  },
  { key: 'carriageway', label: 'Carriageway width', unit: 'm', cell: 'LAYOUT!F30' },
  { key: 'wearingCourse', label: 'Wearing course', unit: 'mm', cell: 'GENERAL!E17' },
  {
    key: 'barrierRail',
    label: 'Barrier rail weight',
    unit: 'kN/m',
    cell: 'GENERAL!H21',
    hint: 'One rail. The workbook spreads it over the slab width and multiplies by 1.25 — correction C14.',
  },
  { key: 'concreteUnitWeight', label: 'Concrete unit weight', unit: 'kN/m³', cell: 'GENERAL!H22' },
  { key: 'asphaltUnitWeight', label: 'Asphalt unit weight', unit: 'kN/m³', cell: 'GENERAL!H23' },
  {
    key: 'effectiveWidth',
    label: 'Strip width beff',
    unit: 'mm',
    cell: 'CCLFM!H29',
    hint: 'A flat slab is rated as a strip. 1000 mm is the workbook’s own choice.',
  },
  {
    key: 'steelArea',
    label: 'Main reinforcement As',
    unit: 'mm²',
    cell: 'CCLFM!J9',
    hint: 'Over the strip, before any section loss.',
  },
  {
    key: 'centroidFromBottom',
    label: 'Reinforcement centroid yb',
    unit: 'mm',
    cell: 'CCLFM!J10',
    hint: 'Above the soffit. The effective depth is derived from this and the thickness.',
  },
  {
    key: 'sectionLoss',
    label: 'Section loss',
    unit: '%',
    cell: 'CCLFM!H25',
    hint: 'Of the main reinforcement. Zero means inspected and none found — which is why it is asked rather than assumed.',
  },
];

/**
 * The RCDG form: a T-beam girder, its share of the deck, and its stirrups.
 *
 * NINETEEN VALUES against the flat slab's twelve, and the extra seven are what
 * the family is about — the web and its fillets, the girder spacing that sets
 * the distribution factor, and the three separate damage inputs. See the header
 * of src/cbplr/rcdg.ts.
 */
export const RCDG_FIELDS: readonly FieldSpec[] = [
  { key: 'span', label: 'Span length L', unit: 'm', cell: 'LAYOUT!F33' },
  { key: 'girderWidth', label: 'Girder web width bw', unit: 'm', cell: 'LAYOUT!F28' },
  {
    key: 'webHeight',
    label: 'Girder web height h',
    unit: 'm',
    cell: 'LAYOUT!F29',
    hint: 'Below the slab soffit. The slab is counted separately, so this is the web alone.',
  },
  { key: 'filletWidth', label: 'Fillet width wf', unit: 'm', cell: 'LAYOUT!F30' },
  { key: 'filletHeight', label: 'Fillet height hf', unit: 'm', cell: 'LAYOUT!F31' },
  { key: 'slabThickness', label: 'Deck slab thickness ts', unit: 'm', cell: 'LAYOUT!F32' },
  {
    key: 'girderSpacing',
    label: 'Girder spacing S',
    unit: 'm',
    cell: 'GENERAL!E16',
    hint: 'On centres. This sets the distribution factor as well as the width of deck the girder carries.',
  },
  {
    key: 'girderCount',
    label: 'Number of girders',
    unit: '',
    cell: 'GENERAL!E15',
    hint: 'Used only to share the two barrier rails across the deck.',
  },
  { key: 'barrierRail', label: 'Barrier rail weight', unit: 'kN/m', cell: 'GENERAL!H22', hint: 'ONE rail. The workbook shares two of them over every girder.' },
  { key: 'wearingCourse', label: 'Wearing course', unit: 'mm', cell: 'GENERAL!E18' },
  { key: 'concreteUnitWeight', label: 'Concrete unit weight', unit: 'kN/m³', cell: 'GENERAL!H23' },
  { key: 'asphaltUnitWeight', label: 'Asphalt unit weight', unit: 'kN/m³', cell: 'GENERAL!H24' },
  { key: 'steelArea', label: 'Main reinforcement As', unit: 'mm²', cell: 'CCLFM!J7' },
  { key: 'centroidFromBottom', label: 'Reinforcement centroid yb', unit: 'mm', cell: 'CCLFM!J8', hint: 'Above the girder soffit.' },
  { key: 'sectionLoss', label: 'Section loss, main steel', unit: '%', cell: 'CCLFM!H24' },
  { key: 'stirrupArea', label: 'Stirrup bar area Av', unit: 'mm²', cell: 'CCLFM!J12' },
  { key: 'stirrupSpacing', label: 'Stirrup spacing S', unit: 'mm', cell: 'CCLFM!J13' },
  { key: 'stirrupSectionLoss', label: 'Section loss, stirrups', unit: '%', cell: 'CCLFM!H38' },
  {
    key: 'shearCrackDepth',
    label: 'Shear crack depth',
    unit: 'mm',
    cell: 'CCLFM!H34',
    hint: 'Into the web. Past half the web width the concrete is credited with no shear capacity at all.',
  },
];

/** The cantilever an EXTERIOR girder carries. Refused on an interior one. */
export const RCDG_CANTILEVER_FIELDS: readonly FieldSpec[] = [
  { key: 'cantilever.thickness', label: 'Cantilever slab thickness', unit: 'm', cell: 'LAYOUT!F33' },
  { key: 'cantilever.haunch', label: 'Cantilever haunch', unit: 'm', cell: 'LAYOUT!F34' },
  { key: 'cantilever.length', label: 'Cantilever length', unit: 'm', cell: 'LAYOUT!F35' },
  {
    key: 'cantilever.railingWidth',
    label: 'Railing and footway width',
    unit: 'm',
    cell: 'LAYOUT!F36',
    hint: 'Comes off the asphalt width — a footpath carries no wearing course — but not off the concrete.',
  },
];

/** The four material values, kept apart because they are what the era table fills. */
export const MATERIAL_FIELDS: readonly FieldSpec[] = [
  { key: 'concrete.strength', label: 'Concrete f′c', unit: 'MPa', cell: 'GENERAL!F20' },
  {
    key: 'concrete.allowable.inventory',
    label: 'Concrete fc, inventory',
    unit: 'MPa',
    cell: 'CCASM!H8',
  },
  {
    key: 'concrete.allowable.operating',
    label: 'Concrete fc, operating',
    unit: 'MPa',
    cell: 'CCASM!H9',
  },
  { key: 'rebar.strength', label: 'Reinforcement fy', unit: 'MPa', cell: 'GENERAL!I20' },
  {
    key: 'rebar.allowable.inventory',
    label: 'Reinforcement fs, inventory',
    unit: 'MPa',
    cell: 'CCASM!H11',
  },
  {
    key: 'rebar.allowable.operating',
    label: 'Reinforcement fs, operating',
    unit: 'MPa',
    cell: 'CCASM!H12',
  },
];

/**
 * The PCDG form: a precast I-girder, its share of the deck, and its tendons.
 *
 * THE LONGEST FORM IN THE APPLICATION — thirty values against RCDG's nineteen —
 * and the section alone is ten of them, because a prestressed I-girder is not
 * described by a width and a depth. They are asked in the order the drawing
 * gives them: the span and the framing first, then down the section from the top
 * flange to the soffit, then the tendons, the materials and the loads.
 *
 * NO ALLOWABLE-STRESS FIELDS, unlike the two RC families. PCDG's serviceability
 * limits are fractions of f′c written into the method — 0.6 f′c, 0.4 f′c and
 * 0.5 √f′c — rather than era-table allowables an engineer looks up, so the form
 * asks for the cylinder strengths and nothing else. That is why `fieldsFor` does
 * not append `MATERIAL_FIELDS` for this family.
 */
export const PCDG_FIELDS: readonly FieldSpec[] = [
  { key: 'span', label: 'Span length L', unit: 'm', cell: 'GENERAL!E13' },
  {
    key: 'girderSpacing',
    label: 'Girder spacing S',
    unit: 'm',
    cell: 'GENERAL!E16',
    hint: 'On centres. This sets the live-load distribution factor and nothing else — the width of deck the girder carries is asked separately below.',
  },
  { key: 'girderCount', label: 'Number of girders', unit: 'nos', cell: 'GENERAL!E15' },
  {
    key: 'slabWidth',
    label: 'Deck width carried',
    unit: 'm',
    cell: 'LAYOUT!E45',
    hint: 'Equal to the spacing on an interior girder and wider on an exterior one. It is also the effective flange width the ultimate moment uses.',
  },

  { key: 'section.height', label: 'Girder depth', unit: 'm', cell: 'LAYOUT!E43' },
  { key: 'section.slabThickness', label: 'Deck slab thickness', unit: 'm', cell: 'LAYOUT!F45' },
  { key: 'section.upperFlangeWidth', label: 'Top flange width', unit: 'm', cell: 'LAYOUT!E46' },
  {
    key: 'section.upperFlangeThickness',
    label: 'Top flange thickness',
    unit: 'm',
    cell: 'LAYOUT!F46',
  },
  { key: 'section.taperDepth', label: 'Top flange taper depth', unit: 'm', cell: 'LAYOUT!F47' },
  {
    key: 'section.fillet',
    label: 'Web fillet',
    unit: 'm',
    cell: 'LAYOUT!E49',
    hint: 'Square, so one dimension. The taper width is derived from the flange width, the web and this.',
  },
  { key: 'section.webWidth', label: 'Web width bw', unit: 'm', cell: 'LAYOUT!E50' },
  { key: 'section.lowerFillet', label: 'Bottom flange fillet', unit: 'm', cell: 'LAYOUT!E51' },
  { key: 'section.lowerFlangeWidth', label: 'Bottom flange width', unit: 'm', cell: 'LAYOUT!E52' },
  {
    key: 'section.lowerFlangeThickness',
    label: 'Bottom flange thickness',
    unit: 'm',
    cell: 'LAYOUT!F52',
  },

  {
    key: 'prestress.workingForce',
    label: 'Working force after losses',
    unit: 'kN',
    cell: 'GENERAL!G23',
  },
  {
    key: 'prestress.centroidFromSoffit',
    label: 'Tendon centroid at midspan',
    unit: 'mm',
    cell: 'GENERAL!G24',
    hint: 'Above the soffit. The eccentricity is derived from this and the girder centroid.',
  },
  { key: 'prestress.ultimateStrength', label: 'Tendon fu', unit: 'MPa', cell: 'GENERAL!G22' },
  { key: 'prestress.yieldStrength', label: 'Tendon fy', unit: 'MPa', cell: 'CCMS!H17' },
  {
    key: 'prestress.lossFraction',
    label: 'Assumed loss fraction',
    unit: '',
    cell: 'CCMS!H8',
    hint: 'The workbook’s 0.03. Read correction C19 before changing it — the sheet multiplies this by the working force in kN and uses the result as a stress in MPa, which is not what the field appears to be.',
  },
  {
    key: 'prestress.sectionLoss',
    label: 'Tendon section loss',
    unit: '%',
    cell: 'CCMS!H13',
    hint: 'Zero means inspected and none found, which is why it is asked rather than assumed.',
  },

  { key: 'girderStrength', label: 'Girder f′c', unit: 'MPa', cell: 'GENERAL!G20' },
  { key: 'slabStrength', label: 'Deck f′c', unit: 'MPa', cell: 'GENERAL!G21' },
  { key: 'rebarYield', label: 'Stirrup fy', unit: 'MPa', cell: 'GENERAL!G27' },

  { key: 'stirrupDiameter', label: 'Stirrup diameter', unit: 'mm', cell: 'CCMS!I26' },
  { key: 'stirrupSpacing', label: 'Stirrup spacing', unit: 'mm', cell: 'CCMS!I28' },
  { key: 'stirrupSectionLoss', label: 'Stirrup section loss', unit: '%', cell: 'CCMS!I29' },
  {
    key: 'shearCrackDepth',
    label: 'Shear crack depth',
    unit: 'mm',
    cell: 'CCMS!I24',
    hint: 'Into the web. At half the web width the concrete is credited with no shear capacity at all.',
  },

  { key: 'diaphragm', label: 'Diaphragm weight', unit: 'kN/m', cell: 'DFDLLL!H13' },
  {
    key: 'barrierRail',
    label: 'Barrier rail weight',
    unit: 'kN/m',
    cell: 'GENERAL!G26',
    hint: 'One rail. Two are shared across every girder.',
  },
  { key: 'wearingCourse', label: 'Wearing course', unit: 'mm', cell: 'GENERAL!E18' },
  { key: 'concreteUnitWeight', label: 'Concrete unit weight', unit: 'kN/m³', cell: 'GENERAL!G28' },
  { key: 'asphaltUnitWeight', label: 'Asphalt unit weight', unit: 'kN/m³', cell: 'GENERAL!G29' },
];

/** The one field an exterior PCDG girder adds. */
export const PCDG_SIDEWALK_FIELDS: readonly FieldSpec[] = [
  {
    key: 'sidewalkWidth',
    label: 'Sidewalk width',
    unit: 'm',
    cell: 'LAYOUT!E53',
    hint: 'Comes off the asphalt width — a footway carries no wearing course — and off nothing else.',
  },
];

/**
 * The fields this family and position actually needs — the form's whole shape.
 *
 * ONE FUNCTION, used by the form, by the assembler and by the tests, so a field
 * cannot be drawn and not read, or read and not drawn.
 */
export function fieldsFor(
  family: RatingFamily,
  position: GirderPosition | null,
): readonly FieldSpec[] {
  if (family === 'RCDG') {
    return [
      ...RCDG_FIELDS,
      ...(position === 'exterior' ? RCDG_CANTILEVER_FIELDS : []),
      ...MATERIAL_FIELDS,
    ];
  }
  /* No MATERIAL_FIELDS — see the header of PCDG_FIELDS. */
  if (family === 'PCDG') {
    return [...PCDG_FIELDS, ...(position === 'exterior' ? PCDG_SIDEWALK_FIELDS : [])];
  }
  return [...RCFS_FIELDS, ...MATERIAL_FIELDS];
}

export function emptyDraft(): RatingDraft {
  return {
    bridgeId: null,
    family: 'RCFS',
    continuity: 'simple',
    position: null,
    spanIndex: '',
    /* Not pre-filled. The unit weights are the tempting exception — 24 and 22 in
       every workbook in the pack — and they are left empty for the same reason
       as everything else: a value the application supplied and the engineer
       never looked at is attributed to the engineer on the report. The form
       offers them as a one-click fill instead, which records `user`. */
    fields: {},
    sources: {},
    note: '',
    inspectionId: null,
    damageBasis: {},
  };
}

/** The unit weights every workbook in the reference pack uses. Offered, not applied. */
export const CONVENTIONAL: Readonly<Record<string, string>> = {
  concreteUnitWeight: '24',
  asphaltUnitWeight: '22',
  effectiveWidth: '1000',
};

export type Backend = 'unknown' | 'live' | 'unreachable' | 'signed-out' | 'behind';

export interface State {
  screen: Screen;
  profile: StandardsProfile;

  /** The register, shared with Bridge Watch. Null until it has been read. */
  bridges: readonly Bridge[] | null;
  /** Ratings already recorded, for the whole department. Null until read. */
  ratings: readonly RatingRecord[] | null;
  /**
   * The department's defects, for the damage inputs (LC-20) and the re-rating
   * flag (LC-24). Null until read, or where the deployment would not say.
   */
  defects: readonly RegisterDefect[] | null;

  draft: RatingDraft;
  /** The rating just saved, shown on the result panel. */
  saved: RatingRecord | null;

  /**
   * What the structure's cloud model last said, including what it could not say.
   *
   * Kept on the state rather than folded into the draft because it is a REPORT
   * about a read, not part of the rating: it survives editing the fields it
   * filled, and its notes stay on screen while the engineer acts on them.
   */
  modelFill: ModelFillReport | null;

  backend: Backend;
  /** What the deployment said, when it said something unwelcome. */
  backendNote: string | null;
  identity: string | null;
  role: string | null;
  capabilities: readonly Capability[] | null;

  busy: boolean;
  error: string | null;
  /** Which structure the register list has selected. */
  selected: string | null;
  /**
   * The rating on record the Ratings screen has open, drawn in place of the
   * one the structure stands on — its address, `?rating=` (LC-31). Null for
   * the governing rating.
   */
  opened: string | null;
  search: string;
  /** Which vehicle the rating-factor chart is drawn for. The rating truck until somebody picks another. */
  chartVehicle: VehicleId;
}

export function initialState(): State {
  return {
    /* Not the register: the page's explorer and 3D model have already chosen
       the structure, so what Bridge Rating opens on is its ratings. */
    screen: 'ratings',
    profile: DPWH_CBPLR,
    bridges: null,
    ratings: null,
    defects: null,
    draft: emptyDraft(),
    saved: null,
    modelFill: null,
    backend: 'unknown',
    backendNote: null,
    identity: null,
    role: null,
    capabilities: null,
    busy: false,
    error: null,
    selected: null,
    opened: null,
    search: '',
    chartVehicle: 'MS18',
  };
}

type Listener = (s: Readonly<State>) => void;

/**
 * The store. Coalesces to one paint per frame.
 *
 * Deliberately small: a `set`, a `subscribe`, and a render on the next animation
 * frame. Bridge Watch's is larger because it owns three map scenes whose
 * lifecycle has to be sequenced against paints; nothing here does.
 */
class Store {
  private state: State = initialState();
  private listeners = new Set<Listener>();
  private queued = false;

  get(): Readonly<State> {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  set(patch: Partial<State>): void {
    this.state = { ...this.state, ...patch };
    this.schedule();
  }

  private schedule(): void {
    if (this.queued) return;
    this.queued = true;
    const paint = () => {
      this.queued = false;
      for (const fn of this.listeners) fn(this.state);
    };
    /* requestAnimationFrame where there is one. Under the test harness there is
       not, and a store that silently never painted would make every screen check
       assert against an empty document. */
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(paint);
    else paint();
  }
}

export const store = new Store();

/** Whether the signed-in person may do this. Null capabilities means "not asked yet". */
export function can(s: Readonly<State>, capability: Capability): boolean {
  return s.capabilities === null ? false : s.capabilities.includes(capability);
}

/**
 * One sentence saying why a control is unavailable.
 *
 * Written out rather than a disabled button with no explanation, which is the
 * single most common way an application wastes somebody's afternoon.
 */
export function whyNot(s: Readonly<State>, what: string): string {
  if (s.backend === 'signed-out') return `Sign in to ${what}.`;
  if (s.backend === 'behind') return `This deployment cannot ${what} until its database is migrated.`;
  if (s.capabilities === null) return `Nobody has said whether you may ${what}.`;
  return `Your roles (${s.role ?? 'unknown'}) may not ${what}.`;
}
